import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import gsap from 'gsap';
import DJPalProgressBar from './DJPalProgressBar';
import DJPalScrollableList from './DJPalScrollableList';
import DJPalAudioPreview from './DJPalAudioPreview';
import ErrorBanner from '../ErrorBanner';
import { isSupportedMixUrl, canPlayMix, useLazyMixResolveMode, useMixPlayback, delayedClick } from './MixPlayer';
import { setMixQuery, setMixResults, toggleExpandedMix, setTrackQuery, setArtistQuery, setActiveSearch, setMixFilterContext } from '../../redux/slices/djpalSlice';
import { searchMixes, searchTrack, searchArtist, loadMoreMixes, fetchFullMixTracklist } from '../../redux/thunk/djpalThunk';
import { selectHistoryByType } from '../../redux/slices/searchHistorySlice';
import { addSearchHistoryEntry } from '../../redux/thunk/searchHistoryThunk';
import { addTrack } from '../../redux/slices/musicPlayerSlice';
import { generateShareImage, shareImage } from '../../utils/generateShareImage';
import { buildMixShareUrl, copyLink, shareLink } from '../../utils/shareLinks';
import ShareMenu from './ShareMenu';
import { useToast } from '../../contexts/ToastContext';
import MixLikeButton from '../MixLikeButton';
import MixContextMenu from '../MixContextMenu';
import LikeButton from '../LikeButton';
import TrackContextMenu from '../TrackContextMenu';
import SupportedCheck from '../common/SupportedCheck';
import useRowContextMenu from '../../hooks/useRowContextMenu';
import { useIsNarrow } from '../../hooks/useIsNarrow';
import { useTierGate } from '../../hooks/useTierGate';
import { showUpgradeModal } from '../../redux/slices/tierSlice';
import { GoatBadge } from '../Tier';
import { isWeb as _isWeb, isNative, notifyWebOnly } from '../../utils/webOnly';
import { resolveMixCover, useNativeMixCoverMode } from '../../utils/nativeMixCover';
import { ReactComponent as PlayIcon } from '../../assets/icons/play.svg';
import { ReactComponent as PauseIcon } from '../../assets/icons/pause.svg';
import { useDesignV2 } from '../../utils/designV2';
import HeroHeader from '../v2/HeroHeader';
import { safeArtwork } from '../../utils/no1001';
import { fmtNum, relativeDate } from '../v2/format';
import HighlightText from './HighlightText';
import MultiArtistText from '../MultiArtistText';
import EntityHoverCard from '../EntityHoverCard';
import ArtistsListModal from './ArtistsListModal';
import MixesListModal from './MixesListModal';
import { entityId } from '../../utils/entityId';
import { eventMixesHeading, eventKindLabel } from '../../utils/searchSections';
import PageTabs, { SectionHeader } from '../v2/PageTabs';
import PlayAllButton from '../common/PlayAllButton';
import CoverArt from '../CoverArt';
import ArtistLinksRow, { orderedLinks, mixLinkRows, MIX_SOURCES } from './ArtistLinksRow';
import { track as trackEvent } from '../../utils/analytics';
// DESIGN V2 event page: what shows before See all.
const EVENT_DJS_PREVIEW = 8;
const EVENT_MIXES_PREVIEW = 10;
const EVENT_TABS = [{ key: 'overview', label: 'Overview' }, { key: 'mixes', label: 'Mixes' }, { key: 'djs', label: 'DJs' }];

// Return deduped artists with full data (artworkUrl, popularityScore, etc.)
// when the server attached track.artists[]. Falls back to plain-name objects
// from track.artist text when the rich data isn't available.
const getTracklistArtists = (tracklist) => {
    if (!tracklist?.length) return [];
    const byKey = new Map();
    for (const entry of tracklist) {
        const t = entry.track || entry;
        const arr = Array.isArray(t.artists) && t.artists.length ? t.artists : null;
        if (arr) {
            for (const a of arr) {
                const key = (a.id != null ? `id:${a.id}` : (a.name || '').toLowerCase());
                if (!key || byKey.has(key)) continue;
                byKey.set(key, {
                    id: entityId(a),
                    name: a.name,
                    artworkUrl: a.artworkUrl || null,
                    popularityScore: typeof a.popularityScore === 'number' ? a.popularityScore : 0,
                });
            }
        } else if (t.artist) {
            const key = t.artist.toLowerCase();
            if (!byKey.has(key)) byKey.set(key, { name: t.artist, popularityScore: 0 });
        }
    }
    return Array.from(byKey.values());
};

const FREE_RESULTS_LIMIT = 5;
const TRACKLIST_LIMIT = 30;
// Max scraped style/genre labels shown in the mix header (desktop + mobile).
const MIX_STYLES_SHOWN = 2;

const MixSearch = () => {
    const dispatch = useDispatch();
    const toast = useToast();
    // Re-render when the server-sourced lazy-mix mode flips so the
    // `canPlayMix(mix)` calls below pick up the new value.
    useLazyMixResolveMode();
    useNativeMixCoverMode(); // re-render when the native cover flag flips
    const { canAccess } = useTierGate('full-mixpedia');
    const { mixQuery, mixResults, mixResultsPagination, mixFilterContext, expandedMixIds, isSearching, searchError, dateRange, pendingMix } = useSelector(state => state.djpal);
    const searchTerm = useSelector(state => state.app.searchTerm);
    const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
    const resultsRef = useRef(null);
    const [fetchedArtwork, setFetchedArtwork] = useState({});
    const [copiedTrackId, setCopiedTrackId] = useState(null);
    const [expandedTracklistIds, setExpandedTracklistIds] = useState([]);
    const [allArtistsModal, setAllArtistsModal] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    // DESIGN V2 event page tab. Declared up here: the load-more observer below must
    // re-arm when the Mixes tab mounts the sentinel (the Overview tab has none) —
    // page 2 of a festival's sets never loaded on the v2 page otherwise.
    const [eventTab, setEventTab] = useState('overview');
    // Right-click on a mix card / tracklist row (or the mix header) opens the
    // same menu as its ⋯ button.
    const { openRowCtx, rowCtxFor } = useRowContextMenu();
    const isNarrow = useIsNarrow();
    const v2 = useDesignV2(); // DESIGN V2 — hero header on the expanded mix
    const fetchedFullTracklistsRef = useRef(new Set());
    const isWeb = _isWeb;
    const { playMix, loadingMixId, playingMixId } = useMixPlayback();

    const handleMixPlay = (mix, playContext = null) => {
        if (isWeb) {
            notifyWebOnly('mix');
            return;
        }
        playMix(mix, playContext);
    };









    const handleToggleTracklist = (e, mixId) => {
        e.stopPropagation();
        dispatch(toggleExpandedMix(mixId));
    };

    const handleArtistClick = useCallback((artistName, artistId = null, artworkUrl = null) => {
        dispatch(setArtistQuery(artistName));
        dispatch(setActiveSearch('artist'));
        dispatch(searchArtist({ artistName, artistId: artistId || null, artworkUrl: artworkUrl || null }));
    }, [dispatch]);

    // Hover preview (photo + genre + Follow) on artist credit links — same
    // card as the For You feed. Touch devices render `node` untouched.
    const artistHoverWrap = useCallback((a, node) => (
        <EntityHoverCard
            type="artist"
            entity={a}
            onNavigate={() => handleArtistClick(a.name, entityId(a), a.artworkUrl)}
        >
            {node}
        </EntityHoverCard>
    ), [handleArtistClick]);


    const handleSearchTrack = useCallback((e, track) => {
        e.stopPropagation();
        const query = track.title && track.artist ? `${track.artist} - ${track.title}` : (track.title || '');
        dispatch(setTrackQuery(query));
        dispatch(setActiveSearch('track'));
        dispatch(searchTrack({ trackName: query, trackId: entityId(track), variant: 'A', artworkUrl: track.artwork || track.artworkUrl || track.image || null }));
    }, [dispatch, dateRange]);

    const handleAddToQueue = (e, track) => {
        e.stopPropagation();
        const src = track.previewUrl || track.sampleUrl || track.preview_url || track.preview;
        if (!src) return;
        const art = track.artwork || track.artworkUrl || track.image || '';
        dispatch(addTrack({
            name: track.title || 'Unknown Track',
            singer: track.artist || '',
            cover: art,
            musicSrc: src,
        }));
    };

    const handleCopyTrack = useCallback((e, track, trackId) => {
        e.stopPropagation();
        const text = track.artist ? `${track.artist} - ${track.title}` : (track.title || '');
        navigator.clipboard.writeText(text);
        setCopiedTrackId(trackId);
        setTimeout(() => setCopiedTrackId(null), 1500);
    }, []);

    // Fetch artwork via yt-dlp when a mix is expanded and has no artwork
    const fetchArtworkForMix = useCallback(async (mix) => {
        if (!mix?.id || fetchedArtwork[mix.id] !== undefined) return;
        if (mix.artwork || mix.artworkUrl || mix.image) return;
        if (!mix.link || !isSupportedMixUrl(mix.link)) return;
        // Mark as loading
        setFetchedArtwork(prev => ({ ...prev, [mix.id]: null }));
        try {
            const result = await window.electron.djpal.extractStreamUrl(mix.link);
            if (result?.thumbnail) {
                setFetchedArtwork(prev => ({ ...prev, [mix.id]: result.thumbnail }));
            }
        } catch (err) {
            console.error('[MixSearch] artwork fetch error:', err);
        }
    }, [fetchedArtwork]);

    // Fresh search: staggered fade-in for polish.
    // Infinite-scroll append: NO animation — items render at default opacity.
    //
    // The "fresh search" signal is mixResults === null, which the thunk
    // dispatches at the start of every new search. Detecting freshness via
    // a separate useEffect on mixFilterContext.kind raced this effect: the
    // reset ran AFTER this one had stamped prevMixCountRef = 50, leaving
    // prev = 0 for the next render — so page-2 appends were mis-classified
    // as fresh and the whole list re-animated from opacity:0 (visible to the
    // user as a blank/black viewport while items 1-100 staggered back in).
    // Freshness must come from the explicit null→results transition, NOT a
    // length heuristic: in-place updates that keep the same length (e.g. the
    // lazy full-tracklist landing via updateMixTracklist) replaced the
    // mixResults reference and re-ran the dissolve — the whole card blinked
    // ("the artwork reloaded") a beat after it appeared.
    const freshSearchRef = useRef(true);
    useEffect(() => {
        if (mixResults === null) {
            freshSearchRef.current = true; // next non-null set = fresh search
            return;
        }
        if (!freshSearchRef.current) return; // append / in-place update — no anim
        if (!resultsRef.current) return;
        const items = resultsRef.current.children;
        if (!items?.length) return;
        freshSearchRef.current = false;
        // Quick dissolve only — the loading skeleton already drew the list
        // structure, so a staggered slide-in reads as a full reload.
        gsap.fromTo(items,
            { opacity: 0 },
            { opacity: 1, duration: 0.25, ease: 'power1.out' },
        );
    }, [mixResults]);

    const allMixes = useMemo(() => (Array.isArray(mixResults) ? mixResults : []), [mixResults]);
    const mixesGated = canAccess ? allMixes : allMixes.slice(0, FREE_RESULTS_LIMIT);
    const mixes = mixesGated;
    const firstMatchRef = useRef(null);
    const matchFoundRef = useRef(false);
    const allMatchRefs = useRef([]);
    const matchIndexRef = useRef(0);
    allMatchRefs.current = [];
    matchFoundRef.current = false;
    const hl = (text) => {
        if (!searchTerm || !text) return text || '';
        const idx = text.toLowerCase().indexOf(searchTerm.toLowerCase());
        if (idx === -1) return text;
        const ref = (el) => { if (el) allMatchRefs.current.push(el); };
        const isFirst = !matchFoundRef.current;
        if (isFirst) matchFoundRef.current = true;
        return <HighlightText text={text} search={searchTerm} matchRef={isFirst ? (el) => { firstMatchRef.current = el; ref(el); } : ref} />;
    };

    useEffect(() => {
        if (searchTerm && firstMatchRef.current) {
            matchIndexRef.current = 0;
            firstMatchRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [searchTerm]);

    useEffect(() => {
        const handleNextMatch = (e) => {
            const matches = allMatchRefs.current;
            if (!matches.length) return;
            matchIndexRef.current = (matchIndexRef.current + e.detail + matches.length) % matches.length;
            matches[matchIndexRef.current]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        };
        window.addEventListener('search-next-match', handleNextMatch);
        return () => window.removeEventListener('search-next-match', handleNextMatch);
    }, []);
    const [exporting, setExporting] = useState(false);

    // Auto-expand when there's exactly 1 result
    useEffect(() => {
        if (mixes.length === 1 && mixes[0]?.id && !expandedMixIds.includes(mixes[0].id)) {
            dispatch(toggleExpandedMix(mixes[0].id));
        }
    }, [mixes]); // eslint-disable-line react-hooks/exhaustive-deps

    // When there's only ONE mix on screen, also auto-expand its full
    // tracklist instead of leaving it behind a "Show all N" collapse —
    // there's nothing else competing for space, so show everything.
    useEffect(() => {
        if (mixes.length !== 1) return;
        const only = mixes[0];
        const tl = only?.tracklist || only?.tracksIncluded;
        if (only?.id && Array.isArray(tl) && tl.length > TRACKLIST_LIMIT
            && !expandedTracklistIds.includes(only.id)) {
            setExpandedTracklistIds(prev => [...prev, only.id]);
        }
    }, [mixes]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reset full-tracklist fetch tracking when filter context changes
    useEffect(() => {
        fetchedFullTracklistsRef.current = new Set();
    }, [mixFilterContext?.kind, mixFilterContext?.artistName, mixFilterContext?.trackName]);

    // In 'featuring' and 'trackIncluded' modes, mixes come back with
    // `tracksIncluded` only (server returns matched tracks, not the full
    // tracklist — sometimes not even that). Fetch the full tracklist on
    // expand so the user sees the complete set list — without this the
    // plays-pill click showed "Tracklist not dropped yet" on a mix whose
    // 20-row tracklist is what produced the play count in the first place.
    // Note: public API sanitizer re-exposes `publicId` as `id`, so mix.id
    // is already the public_id string we pass to the server.
    useEffect(() => {
        if (!['featuring', 'trackIncluded', 'title'].includes(mixFilterContext?.kind)) return;
        for (const id of expandedMixIds) {
            if (fetchedFullTracklistsRef.current.has(id)) continue;
            const mix = mixes.find(m => m.id === id);
            if (!mix || mix.tracklist || !mix.id) continue;
            fetchedFullTracklistsRef.current.add(id);
            dispatch(fetchFullMixTracklist({ mixId: mix.id, publicId: mix.id }));
        }
    }, [expandedMixIds, mixes, mixFilterContext, dispatch]);

    // Infinite-scroll pagination for filtered mix results.
    // Uses a sentinel at the bottom + IntersectionObserver so it works
    // regardless of which ancestor is the actual scroll container.
    const loadMoreSentinelRef = useRef(null);
    useEffect(() => {
        if (!mixFilterContext || !mixResultsPagination?.hasNext) return;
        const el = loadMoreSentinelRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(async (entries) => {
            if (!entries[0].isIntersecting || loadingMore) return;
            setLoadingMore(true);
            // Page 2+ of an artist's mixes must stay on the SAME artist —
            // the context carries the public id (ArtistSearch / results
            // page set it); the name alone can resolve to a namesake.
            const artistIdPart = mixFilterContext.artistPublicId ? { artist_id: mixFilterContext.artistPublicId } : {};
            const filters = mixFilterContext.kind === 'by'
                ? { artist: mixFilterContext.artistName, ...artistIdPart }
                : mixFilterContext.kind === 'featuring'
                    ? { artistIncluded: mixFilterContext.artistName, ...artistIdPart }
                    : mixFilterContext.kind === 'title'
                        // a source page (club / festival / radio / podcast) pages by its id
                        ? (mixFilterContext.sourceId ? { source: mixFilterContext.sourceId } : { title: mixFilterContext.titleQuery })
                        // Page 2+ of a track's mixes must stay on the SAME
                        // track — keep the id when the context carries one,
                        // the name is only the server-side fallback.
                        : mixFilterContext.trackId
                            ? { trackId: mixFilterContext.trackId, _trackName: mixFilterContext.trackName }
                            : { trackIncluded: mixFilterContext.trackName };
            await dispatch(loadMoreMixes({
                filters,
                dateRange,
                page: (mixResultsPagination?.page || 1) + 1,
                lite: mixFilterContext.kind === 'title',
            }));
            setLoadingMore(false);
        }, { rootMargin: '400px' });
        observer.observe(el);
        return () => observer.disconnect();
    }, [dispatch, mixFilterContext, mixResultsPagination, dateRange, loadingMore, eventTab]);

    const handleExport = useCallback(async (mix) => {
        if (exporting) return;
        setExporting(true);
        try {
            const art = mix.artwork || mix.artworkUrl || mix.image || fetchedArtwork[mix.id] || '';
            const metaParts = [mix.date].filter(Boolean);
            const exportTracklist = (mix.tracklist || mix.tracksIncluded || []).slice(0, 5).map(entry => {
                const t = entry.track || entry;
                return {
                    label: t.artist ? `${t.artist} - ${t.title}` : (t.title || ''),
                    artwork: t.artwork || t.artworkUrl || t.image || '',
                };
            });
            const artists = getTracklistArtists(mix.tracklist || mix.tracksIncluded).map(a => a.name);

            const fullTracklist = mix.tracklist || mix.tracksIncluded || [];
            const blob = await generateShareImage({
                type: 'mix',
                // Title big, DJ as a byline — same hierarchy as the page header.
                name: mix.title || mix.djName || mix.artist || 'Mix',
                byline: mix.title ? (mix.djName || mix.artist || undefined) : undefined,
                artworkUrl: art,
                metaLine1: metaParts.join(' · '),
                stats: [{ value: fullTracklist.length, label: 'Tracks' }],
                tracklist: exportTracklist,
                totalTracksCount: fullTracklist.length,
                tracklistArtists: artists,
                supportedBy: null,
            });
            const safeName = (mix.title || 'mix').replace(/[^a-zA-Z0-9]/g, '_');
            const { method, filePath } = await shareImage(blob, `${safeName}_tracklist.png`);
            const mixLabel = mix.title || 'Mix';
            const savedFolder = filePath ? filePath.split('/').slice(-2)[0] : null;
            if (method === 'clipboard') toast.success(`"${mixLabel}" tracklist summary copied to clipboard`);
            else if (method === 'downloaded') toast.success(`"${mixLabel}" tracklist summary saved to Downloads`);
            else if (method === 'saved') toast.success(`"${mixLabel}" tracklist summary saved${savedFolder ? ` to ${savedFolder}` : ''}`);
            trackEvent('share_image_created', { entity_type: 'mix', entity_id: entityId(mix) || undefined, method });
        } catch (err) {
            console.error('Export failed:', err);
            toast.error('Could not share image');
        } finally {
            setExporting(false);
        }
    }, [exporting, fetchedArtwork, toast]);

    const handleCopyLink = useCallback(async (mix) => {
        const url = buildMixShareUrl(mix.djName || mix.artist, mix.eventName || mix.title);
        if (!url) { toast.error('No shareable link yet'); return 'failed'; }
        const res = await copyLink(url);
        if (res === 'copied') {
            toast.success('Link copied');
            trackEvent('share_link_copied', { entity_type: 'mix', entity_id: entityId(mix) || undefined, method: 'copy' });
        } else if (res === 'failed') toast.error('Could not copy link');
        return res;
    }, [toast]);

    // Mobile "Share page" — open the OS share sheet for the canonical URL.
    const handleSharePage = useCallback(async (mix) => {
        const url = buildMixShareUrl(mix.djName || mix.artist, mix.eventName || mix.title);
        if (!url) { toast.error('No shareable link yet'); return; }
        const res = await shareLink(url, mix.eventName || mix.title);
        if (res === 'copied') toast.success('Link copied');
        else if (res === 'failed') { toast.error('Could not share'); return; }
        trackEvent('share_link_copied', { entity_type: 'mix', entity_id: entityId(mix) || undefined, method: 'native_share' });
    }, [toast]);

    // "Open page" / mobile tap: navigate to the canonical mix URL by re-running
    // the search filtered to this one mix. Returning a single-mix result set
    // renders the detail view directly (see `isExpanded` below) — no fake
    // "focused" state, no special URL flag. The URL effect picks up the new
    // mixQuery and pushes /mix/dj/title onto the history naturally.
    const handleOpenMixPage = useCallback((mix) => {
        const dj = mix.djName || mix.artist || '';
        const title = mix.eventName || mix.title || '';
        if (!dj && !title) return;
        const q = dj && title ? `${dj} - ${title}` : (dj || title);
        // Id-only navigation (link = exact-key fallback); clicks never
        // text-search. The db layer maps numeric `id` → m.id and string
        // `id` → m.public_id, so one key covers both shapes.
        const filters = mix.id ? { id: mix.id }
                       : mix.publicId ? { public_id: mix.publicId }
                       : mix.public_id ? { public_id: mix.public_id }
                       : mix.link ? { link: mix.link }
                       : null;
        if (!filters) return;
        dispatch(setActiveSearch('mix'));
        dispatch(setMixFilterContext(null));
        dispatch(setMixQuery(q));
        dispatch(searchMixes({ filters, dateRange }));
    }, [dispatch, dateRange]);

    // Multi-result list: mobile tap drills into the single-mix detail (via
    // handleOpenMixPage). Desktop keeps inline expand — handy for scanning
    // a list of mixes without leaving the results page.
    const handleMixCardTap = useCallback((mix) => {
        const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
        // DESIGN V2: a list row opens the mix page, like every other entity; inline expansion is the classic desktop behaviour.
        if (isMobile || v2) handleOpenMixPage(mix);
        else dispatch(toggleExpandedMix(mix.id));
    }, [dispatch, handleOpenMixPage, v2]);

    // Pointerup-with-movement-check fires the mix-card tap immediately on
    // finger lift, instead of waiting for the synthesized click event.
    // Going through `click` was eating the first tap on mobile (iOS Safari
    // applies hover-state on any element with declared :hover rules in
    // its tree, and click only fires AFTER the hover-state is settled).
    // Pointer events bypass that machinery: pointerdown → pointerup with
    // <8px movement = tap, cancel otherwise.
    const cardPointerRef = useRef(null);
    const cardPointerHandlers = (mix) => ({
        onPointerDown: (e) => {
            // Right-click opens the row's context menu (onContextMenu on the
            // card) — it must not also count as a tap and open the mix page.
            if (e.button !== 0) {
                cardPointerRef.current = null;
                return;
            }
            // Skip if the touch started on a nested interactive element
            // (artwork play overlay, ⋯ menu, like button) — they have
            // their own handlers and stopPropagation.
            if (e.target.closest('button, .track-artwork.clickable, .track-more-wrapper, .track-like-btn')) {
                cardPointerRef.current = null;
                return;
            }
            cardPointerRef.current = { x: e.clientX, y: e.clientY, id: e.pointerId };
        },
        onPointerUp: (e) => {
            const start = cardPointerRef.current;
            cardPointerRef.current = null;
            if (!start || start.id !== e.pointerId) return;
            const dx = e.clientX - start.x;
            const dy = e.clientY - start.y;
            if (dx * dx + dy * dy > 64) return; // > 8px = scroll, not tap
            handleMixCardTap(mix);
        },
        onPointerCancel: () => { cardPointerRef.current = null; },
    });

    // Only show the date-range control once there's a search in flight/committed
    // AND there are 2+ results to refine. With a single result the filter can
    // only narrow the set to zero, so it's noise. Keep it visible whenever a
    // range is already set so the user can see/clear it regardless.
    const mixCount = Array.isArray(mixResults) ? mixResults.length : 0;

    // DESIGN V2 — event / venue / series page: hero + the DJs who played + the
    // sets. Built from the mix list we already fetch; the DJ tiles are derived
    // from the loaded pages (first appearance wins the cover).
    const isEventPage = !!(mixFilterContext && mixFilterContext.kind === 'title' && mixFilterContext.eventKind);
    const eventTotal = mixResultsPagination?.totalCount || 0;
    // The hero art is decided once the sets are in: a single search-card
    // cover swapping to a collage a second later read as a glitch.
    const eventLoaded = allMixes.length > 0 || (!!mixResults && !isSearching);
    const eventCovers = useMemo(() => {
        if (!eventLoaded) return [];
        const fromCtx = Array.isArray(mixFilterContext?.covers) ? mixFilterContext.covers.filter(Boolean) : [];
        const fromMixes = allMixes.map(m => m.artworkUrl).filter(Boolean);
        return [...new Set([...fromMixes, ...fromCtx])].slice(0, 4);
    }, [eventLoaded, mixFilterContext, allMixes]);
    // Same skeleton as the artist page: Overview / Mixes / DJs tabs, See all
    // opens the list modals. Overview shows the first rows of each; the
    // Mixes tab is the full paginated list. Resets when another event opens.
    const [eventModal, setEventModal] = useState(null); // 'djs' | 'mixes'
    useEffect(() => { setEventTab('overview'); setEventModal(null); }, [mixFilterContext?.titleQuery]);
    const eventOverview = v2 && isEventPage && eventTab === 'overview';
    const eventRows = eventOverview ? mixes.slice(0, EVENT_MIXES_PREVIEW) : mixes;
    const eventMixesFolded = eventOverview && (mixes.length > EVENT_MIXES_PREVIEW || !!mixResultsPagination?.hasNext);
    const eventLatest = allMixes.find(m => m.date)?.date || null;
    const eventPlayable = allMixes.find(m => canPlayMix(m)) || null;
    const eventNoun = (n) => (n === 1 ? 'mix' : 'mixes');
    const changeEventTab = (t) => { trackEvent('event_page_tab_changed', { tab: t, event_name: mixFilterContext.titleQuery }); setEventTab(t); };
    const eventDjs = useMemo(() => {
        if (!isEventPage) return [];
        const seen = new Map();
        for (const m of (Array.isArray(mixResults) ? mixResults : [])) {
            const name = (m.djName || m.artist || '').trim();
            if (!name) continue;
            const cur = seen.get(name);
            if (cur) { cur.count += 1; continue; }
            const dj = Array.isArray(m.artists) ? m.artists.find(a => a && a.name === name) : null;
            seen.set(name, { name, id: dj ? entityId(dj) : null, art: m.artworkUrl || null, date: m.date ? relativeDate(m.date) : '', rawDate: m.date || null, count: 1 });
        }
        return [...seen.values()].sort((a, b) => b.count - a.count);
    }, [isEventPage, mixResults]);
    useEffect(() => {
        if (!v2 || !isEventPage) return;
        trackEvent('event_page_view', { event_name: mixFilterContext.titleQuery, kind: mixFilterContext.eventKind, mix_count: eventTotal || undefined });
    // one view per event name
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [v2, isEventPage, mixFilterContext?.titleQuery]);

    return (
        <div className={`djpal-mix-search${v2 ? ' v2-ambient v2-entity-page' : ''}${v2 && isEventPage ? ' v2-event-page' : ''}`}>
            {/* Bar only when results are already on screen (refinements);
                fresh loads paint the real filter title + skeleton rows. */}
            <DJPalProgressBar visible={isSearching && mixCount > 0} />

            {v2 && isEventPage && (
                <>
                    <HeroHeader
                        className="v2-event-hero"
                        art={!eventLoaded
                            ? <div className="v2-event-art-skel" aria-hidden="true" />
                            : eventCovers.length >= 2
                                ? <div className={`v2-event-collage v2-event-collage-${Math.min(eventCovers.length, 4)}`}>{eventCovers.slice(0, 4).map((c, i) => <CoverArt src={c} size={176} eager key={i} />)}</div>
                                : (eventCovers[0] ? eventCovers[0] : <div className="v2-hero-letter">{(mixFilterContext.titleQuery || '?').charAt(0).toUpperCase()}</div>)}
                        ambientSrc={eventCovers[0] || null}
                        ambientKey={mixFilterContext.titleQuery}
                        kicker={eventKindLabel(mixFilterContext.eventKind, mixFilterContext.eventType)}
                        title={mixFilterContext.titleQuery}
                        meta={[
                            eventTotal ? `${fmtNum(eventTotal)} ${eventNoun(eventTotal)}` : null,
                            eventLoaded && mixResultsPagination?.artistCount ? `${fmtNum(mixResultsPagination.artistCount)} DJs` : null,
                        ].filter(Boolean)}
                        sub={eventLoaded && eventDjs.length > 0 ? (
                            <span>
                                <span className="supported-grey">Played by </span>
                                {eventDjs.slice(0, 2).map((d, i) => (
                                    <React.Fragment key={d.name}>
                                        {i > 0 && <span className="supported-grey">, </span>}
                                        <span className="supported-name" onClick={() => handleArtistClick(d.name, d.id || null, d.art || null)}>{d.name}</span>
                                    </React.Fragment>
                                ))}
                                {(() => {
                                    // A number only from the server's distinct count; the loaded
                                    // pages are a sample, so without it say "others", not a figure.
                                    const total = mixResultsPagination?.artistCount;
                                    const rest = total ? total - Math.min(2, eventDjs.length) : 0;
                                    if (rest > 0) return <span className="supported-grey"> and <b>{fmtNum(rest)} others</b></span>;
                                    return eventDjs.length > 2 ? <span className="supported-grey"> and others</span> : null;
                                })()}
                            </span>
                        ) : null}
                        actions={eventLoaded ? (
                            <button type="button" className="v2-btn primary" onClick={() => eventPlayable && handleMixPlay(eventPlayable)} disabled={!eventPlayable} title={eventPlayable ? `Play ${eventPlayable.artist || eventPlayable.djName || ''}` : 'Nothing to play yet'}>
                                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="7 4 20 12 7 20 7 4" /></svg><span>Play</span>
                            </button>
                        ) : null}
                        facts={eventLoaded ? [
                            eventTotal ? { value: fmtNum(eventTotal), label: 'Mixes' } : null,
                            // Only the server's distinct count across every mix; the loaded
                            // pages' count was wrong for any event bigger than one page.
                            mixResultsPagination?.artistCount ? { value: fmtNum(mixResultsPagination.artistCount), label: 'DJs' } : null,
                            eventLatest ? { value: relativeDate(eventLatest), label: 'Latest mix' } : null,
                        ].filter(Boolean) : []}
                    />
                    <div className="v2-tabs-row"><PageTabs className="v2-entity-tabs" items={EVENT_TABS} value={eventTab} onChange={changeEventTab} /></div>
                    {(eventTab === 'overview' || eventTab === 'djs') && eventDjs.length > 0 && (
                        <section className="v2-event-djs">
                            <SectionHeader
                                title={mixFilterContext.eventKind === 'series' ? 'Hosts and guests' : 'DJs who played here'}
                                count={eventTab === 'djs' ? fmtNum(eventDjs.length) : undefined}
                                link={eventTab === 'overview' && eventDjs.length > EVENT_DJS_PREVIEW ? 'See all' : null}
                                onLink={() => { trackEvent('event_page_see_all', { section: 'djs', event_name: mixFilterContext.titleQuery }); setEventModal('djs'); }}
                            />
                            <div className={`v2-event-dj-row${eventTab === 'djs' ? ' open' : ''}`}>
                                {(eventTab === 'djs' ? eventDjs : eventDjs.slice(0, EVENT_DJS_PREVIEW)).map((d) => (
                                    <button type="button" className="v2-event-dj" key={d.name} onClick={() => handleArtistClick(d.name, d.id || null, d.art || null)} title={d.name}>
                                        <span className="v2-event-dj-art">{d.art ? <CoverArt src={d.art} size={120} /> : <span className="v2-event-dj-letter">{d.name.charAt(0).toUpperCase()}</span>}</span>
                                        <span className="v2-event-dj-name">{d.name}</span>
                                        <span className="v2-event-dj-sub">{d.count > 1 ? `${d.count} mixes` : (d.date || '')}</span>
                                    </button>
                                ))}
                            </div>
                        </section>
                    )}
                    {(eventTab === 'overview' || eventTab === 'mixes') && (
                        <SectionHeader
                            title="Latest mixes"
                            count={eventTotal ? fmtNum(eventTotal) : undefined}
                            link={eventMixesFolded ? 'See all' : null}
                            onLink={() => { trackEvent('event_page_see_all', { section: 'mixes', event_name: mixFilterContext.titleQuery }); setEventModal('mixes'); }}
                        />
                    )}
                </>
            )}
            {/* DESIGN V2 — the filtered list ("Mixes by X", "Mixes featuring X",
                "Mixes that played T") opens like a page: the artist's picture,
                the title, how many, and Play. The bare title line read as an
                unfinished page next to the tracks list (Erez 11-02). */}
            {mixFilterContext && v2 && !isEventPage && (() => {
                const kind = mixFilterContext.kind;
                const isArtistList = kind === 'by' || kind === 'featuring';
                const name = isArtistList ? mixFilterContext.artistName : mixFilterContext.trackName;
                const total = mixResultsPagination?.totalCount || (mixResults ? allMixes.length : 0);
                const firstPlayable = allMixes.find(m => canPlayMix(m)) || null;
                const openEntity = () => {
                    if (isArtistList) handleArtistClick(mixFilterContext.artistName, mixFilterContext.artistPublicId || null, mixFilterContext.artworkUrl || null);
                };
                const title = kind === 'by' ? <>Mixes by <span className="v2-list-head-name" onClick={openEntity}>{name}</span></>
                    : kind === 'featuring' ? <>Mixes featuring <span className="v2-list-head-name" onClick={openEntity}>{name}</span></>
                    : kind === 'title' ? (mixFilterContext.eventKind ? eventMixesHeading(mixFilterContext.eventKind, mixFilterContext.titleQuery) : <>Mixes titled &ldquo;{mixFilterContext.titleQuery}&rdquo;</>)
                    : <>Mixes that played <span className="v2-list-head-name">{name}</span></>;
                return (
                    <div className="v2-list-head">
                        {isArtistList && (
                            <button type="button" className="v2-list-head-art" onClick={openEntity} title={name}>
                                {mixFilterContext.artworkUrl
                                    ? <CoverArt src={mixFilterContext.artworkUrl} size={72} eager fallback={<span className="v2-list-head-letter">{(name || '?').charAt(0).toUpperCase()}</span>} />
                                    : <span className="v2-list-head-letter">{(name || '?').charAt(0).toUpperCase()}</span>}
                            </button>
                        )}
                        <div className="v2-list-head-text">
                            <h1 className="v2-list-head-title">{title}</h1>
                            <div className="v2-list-head-meta">
                                {total ? `${fmtNum(total)} ${total === 1 ? 'mix' : 'mixes'}` : (isSearching ? '' : 'No mixes yet')}
                            </div>
                        </div>
                        <div className="v2-list-head-actions">
                            <button type="button" className="v2-btn primary" disabled={!firstPlayable} title={firstPlayable ? 'Play the latest mix' : 'Nothing to play yet'} onClick={() => firstPlayable && handleMixPlay(firstPlayable, { items: mixes, index: Math.max(0, mixes.indexOf(firstPlayable)) })}>
                                <PlayIcon /> Play
                            </button>
                        </div>
                    </div>
                );
            })()}
            {mixFilterContext && !v2 && !isEventPage && (
                <div className="djpal-mix-filter-title">
                    {mixFilterContext.kind === 'trackIncluded' ? (
                        <span>Mixes that played {mixFilterContext.trackName}</span>
                    ) : mixFilterContext.kind === 'title' ? (
                        // eventKind set = arrived via an event/venue/series
                        // result — natural copy beats the literal "titled"
                        // phrasing ("Episodes of Keinemusik Radio Show").
                        <span>
                            {mixFilterContext.eventKind
                                ? eventMixesHeading(mixFilterContext.eventKind, mixFilterContext.titleQuery)
                                : <>Mixes titled &ldquo;{mixFilterContext.titleQuery}&rdquo;</>}
                        </span>
                    ) : (
                        <button
                            type="button"
                            className="clickable-artist"
                            onClick={() => handleArtistClick(mixFilterContext.artistName, mixFilterContext.artistPublicId || null)}
                        >
                            {mixFilterContext.kind === 'by'
                                ? `Mixes by ${mixFilterContext.artistName}`
                                : `Mixes featuring ${mixFilterContext.artistName}`}
                        </button>
                    )}
                </div>
            )}

            {searchError && (
                <ErrorBanner
                    error={searchError}
                    surface="mixpedia_search"
                    onRetry={() => {
                        if (!mixQuery) return;
                        dispatch(searchMixes({ filters: { title: mixQuery }, dateRange, paginate: true }));
                    }}
                />
            )}

            {/* Opening ONE mix: its page's own shape — the hero with the cover,
                title and DJ from the click, then the tracklist — not list rows. */}
            {isSearching && mixCount === 0 && !searchError && v2 && pendingMix && !mixFilterContext && (
                <div className="djpal-mix-expanded v2-mix-pending">
                    <HeroHeader
                        className="v2-mix-hero"
                        art={pendingMix.artwork ? safeArtwork(pendingMix.artwork) || <div className="v2-event-art-skel" aria-hidden="true" /> : <div className="v2-event-art-skel" aria-hidden="true" />}
                        artAlt={pendingMix.title}
                        ambientKey={pendingMix.title}
                        kicker="Mix"
                        title={pendingMix.title}
                        meta={[pendingMix.dj ? <b>{pendingMix.dj}</b> : null, <span className="v2-skel-line sm" aria-hidden="true" />]}
                    />
                    <div className="djpal-skel v2-mix-pending-list">
                        {[0, 1, 2, 3, 4, 5, 6, 7].map(i => (
                            <div className="djpal-skel-track-row" key={i}>
                                <div className="djpal-skel-block skel-art" />
                                <div className="skel-track-lines">
                                    <div className="djpal-skel-block skel-line" />
                                    <div className="djpal-skel-block skel-line short" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {isSearching && mixCount === 0 && !searchError && !(v2 && pendingMix && !mixFilterContext) && (
                <div className="djpal-skel">
                    {[0, 1, 2, 3, 4, 5].map(i => (
                        <div className="djpal-skel-track-row" key={i}>
                            <div className="djpal-skel-block skel-art" />
                            <div className="skel-track-lines">
                                <div className="djpal-skel-block skel-line" />
                                <div className="djpal-skel-block skel-line short" />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {mixResults && allMixes.length === 0 && (
                <div className="djpal-no-results">No mixes found</div>
            )}

            {mixes.length > 0 && !(v2 && isEventPage && eventTab === 'djs') && (
                <div className="djpal-mix-results" ref={resultsRef}>
                    {eventRows.map((mix, mixIdx) => {
                        // Single-result search lands directly on the mix
                        // detail view (no transient "list of one card" step).
                        // The "Open page" path used to use a separate
                        // focusedMixId flag for this; collapsing the two
                        // states avoids the URL/cache duplication.
                        const isSingleResult = mixes.length === 1 && !mixFilterContext;
                        const isExpanded = isSingleResult || expandedMixIds.includes(mix.id);
                        const isLoading = loadingMixId === mix.id;
                        const isPlaying = playingMixId === mix.id;
                        // Native (iOS+Android) may substitute the first track cover (server
                        // flag). On native we never fall back to the lazily-fetched ORIGINAL
                        // mix artwork — resolveMixCover owns the decision.
                        const art = isNative
                            ? (resolveMixCover(mix) || '')
                            : (mix.artwork || mix.artworkUrl || mix.image || fetchedArtwork[mix.id] || '');
                        const tracklist = mix.tracklist || mix.tracksIncluded;
                        const tracklistArtists = getTracklistArtists(tracklist);
                        const djLabel = mix.djName || mix.artist || '';

                        if (isExpanded) {
                            // Trigger artwork fetch if missing. Skip on native — that
                            // fetches the ORIGINAL mix cover, which native must not show.
                            if (!art && !isNative) fetchArtworkForMix(mix);

                            // DESIGN V2 — the mix title without the leading
                            // "DJ - " (the byline carries the DJ) and the
                            // "Including tracks by …" line as the hero sub.
                            const v2CleanTitle = (() => {
                                const stripArtist = (title, artistName) => {
                                    if (!artistName || !title) return title;
                                    return title.replace(new RegExp(`^${artistName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*[-–—@]\\s*`, 'i'), '') || title;
                                };
                                const artistNames = mix.artists?.length > 0 ? mix.artists.map(a => a.name).join(', ') : (djLabel || '');
                                return stripArtist(mix.title, djLabel) || stripArtist(mix.title, artistNames) || mix.title;
                            })();
                            const v2Styles = (mix.styles?.length ? mix.styles : (mix.genres || [])).slice(0, MIX_STYLES_SHOWN).join(', ');
                            const v2CanPlay = canPlayMix(mix);
                            return (
                                <div key={mix.id} className="djpal-mix-expanded">
                                    {v2 ? (
                                        <HeroHeader
                                            className="v2-mix-hero"
                                            art={art || <div className="v2-hero-letter">{(v2CleanTitle || '?').charAt(0).toUpperCase()}</div>}
                                            covers={mix.covers}
                                            artAlt={mix.title || ''}
                                            ambientKey={mix.id}
                                            kicker="Mix"
                                            title={(
                                                <>
                                                    {hl(v2CleanTitle)}
                                                    {mixes.length > 1 && (
                                                        <button className="collapse-mix-btn" onClick={(e) => handleToggleTracklist(e, mix.id)} title="Collapse">
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                            meta={[
                                                (mix.artists?.length > 0 || djLabel) ? (
                                                    <b className="v2-hero-byline">
                                                        {mix.artists?.some((a) => a.artworkUrl) && (
                                                            <span className="v2-hero-byline-faces">
                                                                {mix.artists.filter((a) => a.artworkUrl).slice(0, 3).map((a, i) => (
                                                                    <span key={entityId(a) || `${a.name}-${i}`} className="v2-hero-byline-avatar" title={a.name} onClick={() => handleArtistClick(a.name, entityId(a), a.artworkUrl)}>
                                                                        <CoverArt src={a.artworkUrl} size={48} eager alt="" />
                                                                    </span>
                                                                ))}
                                                            </span>
                                                        )}
                                                        <MultiArtistText
                                                            artists={mix.artists}
                                                            fallbackText={djLabel}
                                                            segmentClassName="clickable-artist"
                                                            onSelect={(a) => handleArtistClick(a.name, entityId(a), a.artworkUrl)}
                                                            segmentWrapper={artistHoverWrap}
                                                            renderName={hl}
                                                        />
                                                    </b>
                                                ) : null,
                                                v2Styles || null,
                                                mix.date ? String(mix.date).slice(0, 10) : null,
                                                tracklist?.length > 0 ? <><b>{fmtNum(tracklist.length)}</b> tracks</> : null,
                                            ]}
                                            sub={tracklistArtists.length > 0 ? (
                                                <span>
                                                    <span className="supported-grey">Including tracks by </span>
                                                    {tracklistArtists.slice(0, 2).map((a, i) => (
                                                        <React.Fragment key={entityId(a) || a.name}>
                                                            {i > 0 && <span className="supported-grey">, </span>}
                                                            {artistHoverWrap(a, (
                                                                <span className="supported-name clickable-artist" onClick={(e) => { e.stopPropagation(); handleArtistClick(a.name, entityId(a)); }}>{a.name}</span>
                                                            ))}
                                                        </React.Fragment>
                                                    ))}
                                                    {tracklistArtists.length > 2 && (
                                                        <>
                                                            <span className="supported-grey"> and </span>
                                                            <span className="supported-others" onClick={(e) => { e.stopPropagation(); setAllArtistsModal({ mixTitle: mix.title || mix.eventName || '', djName: djLabel, artists: tracklistArtists }); }}>{fmtNum(tracklistArtists.length - 2)} others</span>
                                                        </>
                                                    )}
                                                </span>
                                            ) : null}
                                            actions={(
                                                <>
                                                    {/* Two things can play here: the recording of the mix, and its
                                                        tracklist track by track. The labels say which. A mix with no
                                                        recording leads with its tracks instead of a dead Play. */}
                                                    {v2CanPlay || !(tracklist?.length > 0) ? (
                                                        <button type="button" className={`v2-btn primary${isPlaying ? ' playing' : ''}`} disabled={!v2CanPlay} title={v2CanPlay ? (isPlaying ? 'Pause' : 'Play the mix recording') : 'No recording of this mix yet'} {...(v2CanPlay ? delayedClick(() => handleMixPlay(mix, { items: mixes, index: mixIdx })) : {})}>
                                                            {isLoading ? <span className="mix-play-spinner" /> : (isPlaying ? <PauseIcon /> : <PlayIcon />)} {isPlaying ? 'Pause' : (v2CanPlay ? 'Play mix' : 'No recording')}
                                                        </button>
                                                    ) : (
                                                        <PlayAllButton tracks={tracklist.map(e => e.track || e)} from="mix_page_hero" className="v2-btn primary" label="Play tracks" iconSize={14} title="No recording of this mix yet. Play its tracklist, track by track" />
                                                    )}
                                                    <span className="v2-follow-group"><MixLikeButton mix={mix} prominent /></span>
                                                    <ShareMenu onShareImage={() => handleExport(mix)} onSharePage={() => handleSharePage(mix)} onCopyLink={() => handleCopyLink(mix)} sharing={exporting} quiet />
                                                    {orderedLinks(mixLinkRows(mix), MIX_SOURCES).length > 0 && (
                                                        <div className="v2-hero-links">
                                                            <ArtistLinksRow externalIds={mixLinkRows(mix)} artistName={mix.title} event="mix_link_opened" sources={MIX_SOURCES} />
                                                        </div>
                                                    )}
                                                    <span className="v2-hero-menu"><MixContextMenu mix={mix} hideLike openAtPoint={rowCtxFor(`hdr-${mix.id}`)} /></span>
                                                    {mixes.length > 1 && (
                                                        <button type="button" className="v2-btn quiet" onClick={() => handleOpenMixPage(mix)} title="Open mix page">Open page</button>
                                                    )}
                                                </>
                                            )}
                                        />
                                    ) : (<>
                                    {/* Large header — outside card box, matching artist/track tabs */}
                                    {/* Right-click anywhere on the header = the header ⋯ menu. */}
                                    <div
                                        className="djpal-artist-header"
                                        onContextMenu={(e) => {
                                            if (e.target.closest('input, textarea')) return;
                                            openRowCtx(`hdr-${mix.id}`)(e);
                                        }}
                                    >
                                        {/* Two rows: share+heart on top, ⋯ on a second
                                            row beneath them (mobile lands it level with
                                            the byline). On desktop the rows are
                                            display:contents — one inline cluster. */}
                                        <div className="artist-header-actions">
                                            <div className="header-actions-row">
                                                <ShareMenu onShareImage={() => handleExport(mix)} onSharePage={() => handleSharePage(mix)} onCopyLink={() => handleCopyLink(mix)} sharing={exporting} quiet />
                                                {mixes.length > 1 && (
                                                    <button className="open-mix-page-btn" onClick={() => handleOpenMixPage(mix)} title="Open mix page">
                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                                                        <span>Open page</span>
                                                    </button>
                                                )}
                                                <MixLikeButton mix={mix} prominent />
                                            </div>
                                            <div className="header-actions-row">
                                                {/* Header ⋯ — hideLike: the header's
                                                    MixLikeButton is always visible, so
                                                    the menu item is redundant.
                                                    Mobile-only here: desktop keeps its
                                                    pre-cluster slot below (the rows are
                                                    display:contents on desktop, which
                                                    would land this after the Like pill). */}
                                                {isNarrow && <MixContextMenu mix={mix} hideLike openAtPoint={rowCtxFor(`hdr-${mix.id}`)} />}
                                            </div>
                                        </div>
                                        {!isNarrow && (
                                            <div className="artist-header-date-range">
                                                <MixContextMenu mix={mix} hideLike openAtPoint={rowCtxFor(`hdr-${mix.id}`)} />
                                            </div>
                                        )}
                                        {canPlayMix(mix) ? (
                                            <div className="artist-avatar-lg" style={{ borderRadius: 10, position: 'relative', overflow: 'hidden' }}>
                                                <div className={`mix-play-overlay${isLoading ? ' loading' : ''}${isPlaying ? ' playing' : ''}`} {...delayedClick(() => handleMixPlay(mix, { items: mixes, index: mixIdx }))}>
                                                    {isLoading ? <span className="mix-play-spinner" /> : <span className={`play-icon${isPlaying ? ' paused' : ''}`}>{isPlaying ? <PauseIcon /> : <PlayIcon />}</span>}
                                                </div>
                                                {art ? <CoverArt src={art} size={96} eager style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span>&#9835;</span>}
                                            </div>
                                        ) : (
                                            art ? (
                                                <CoverArt className="artist-avatar-lg" style={{ borderRadius: 10 }} src={art} covers={mix.covers} size={96} eager fallback={<div className="artist-avatar-lg" style={{ borderRadius: 10 }}>&#9835;</div>} />
                                            ) : (
                                                <div className="artist-avatar-lg" style={{ borderRadius: 10 }}>&#9835;</div>
                                            )
                                        )}
                                        <div className="artist-info">
                                            <div className="artist-name-lg">
                                                {(() => {
                                                    // Strip leading "Artist - " or "Artist, ... - " from the
                                                    // mix title so we don't render the DJ name twice. The
                                                    // byline below renders the artists separately.
                                                    const stripArtist = (title, artistName) => {
                                                        if (!artistName || !title) return title;
                                                        return title.replace(new RegExp(`^${artistName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*[-–—@]\\s*`, 'i'), '') || title;
                                                    };
                                                    const artistNames = mix.artists?.length > 0 ? mix.artists.map(a => a.name).join(', ') : (djLabel || '');
                                                    const cleanTitle = stripArtist(mix.title, djLabel) || stripArtist(mix.title, artistNames) || mix.title;
                                                    return hl(cleanTitle);
                                                })()}
                                                {/* Single-mix detail: no button — collapse is pointless
                                                    (auto-expand re-opens it), and the old close-× moved
                                                    to the navbar home button / search-clear. */}
                                                {mixes.length > 1 && (
                                                    <button className="collapse-mix-btn" onClick={(e) => handleToggleTracklist(e, mix.id)} title="Collapse">
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
                                                    </button>
                                                )}
                                            </div>
                                            {(mix.artists?.length > 0 || djLabel) && (
                                                <div className="artist-byline">
                                                    {/* MultiArtistText also suppresses links on placeholder
                                                        credits (VA / Unknown Artist) — keep this in sync with
                                                        the list rows by rendering through it. */}
                                                    <MultiArtistText
                                                        artists={mix.artists}
                                                        fallbackText={djLabel}
                                                        segmentClassName="clickable-artist"
                                                        onSelect={(a) => handleArtistClick(a.name, entityId(a), a.artworkUrl)}
                                                        segmentWrapper={artistHoverWrap}
                                                        renderName={hl}
                                                    />
                                                </div>
                                            )}
                                            {orderedLinks(mixLinkRows(mix), MIX_SOURCES).length > 0 && (
                                                <ArtistLinksRow externalIds={mixLinkRows(mix)} artistName={mix.title} event="mix_link_opened" sources={MIX_SOURCES} />
                                            )}
                                            <div className="artist-genre">
                                                {/* Mix "styles" are raw scraped categories (can be many, some
                                                    noise). The server ranks recognized styles first; we show
                                                    only the top few — same as the artist byline / hover card. */}
                                                {[(mix.styles?.length ? mix.styles : (mix.genres || [])).slice(0, MIX_STYLES_SHOWN).join(', '), mix.date].filter(Boolean).join(' · ')}
                                            </div>
                                        </div>
                                    </div>
                                    {tracklistArtists.length > 0 ? (
                                        <div className="artist-supported-line artist-supported-line--below">
                                            <SupportedCheck />
                                            <span>
                                                <span className="supported-grey">Including tracks by </span>
                                                {tracklistArtists.slice(0, 2).map((a, i) => (
                                                    <React.Fragment key={entityId(a) || a.name}>
                                                        {i > 0 && <span className="supported-grey">, </span>}
                                                        {artistHoverWrap(a, (
                                                            <span
                                                                className="supported-name clickable-artist"
                                                                onClick={(e) => { e.stopPropagation(); handleArtistClick(a.name, entityId(a)); }}
                                                            >
                                                                {a.name}
                                                            </span>
                                                        ))}
                                                    </React.Fragment>
                                                ))}
                                                {tracklistArtists.length > 2 && (
                                                    <>
                                                        <span className="supported-grey"> and </span>
                                                        <span
                                                            className="supported-others"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setAllArtistsModal({
                                                                    mixTitle: mix.title || mix.eventName || '',
                                                                    djName: djLabel,
                                                                    artists: tracklistArtists,
                                                                });
                                                            }}
                                                        >
                                                            {tracklistArtists.length - 2} others
                                                        </span>
                                                    </>
                                                )}
                                            </span>
                                        </div>
                                    ) : (['featuring', 'trackIncluded', 'title'].includes(mixFilterContext?.kind) && !Array.isArray(mix.tracklist)) ? (
                                        // Full-tracklist fetch in flight (these contexts arrive
                                        // tracklist-less). It resolves in a few hundred ms — any
                                        // placeholder (hint OR spinner) reads as a flash, so
                                        // render nothing until it lands.
                                        null
                                    ) : (
                                        <div className="djpal-empty-hint" style={{ textAlign: 'left', padding: '6px 0 0' }}>Tracklist not dropped yet</div>
                                    )}
                                    </>)}

                                    {/* Tracklist body */}
                                    {tracklist?.length > 0 && (
                                        <div className="djpal-card" style={{ marginTop: 28 }}>
                                            <h3 style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                Tracklist
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                                                    {(!v2 || v2CanPlay) && (
                                                    <PlayAllButton
                                                        tracks={tracklist.map(e => e.track || e)}
                                                        from="mix_page"
                                                        label={v2 ? 'Play tracks' : 'Play All'}
                                                        title="Play the tracklist, track by track"
                                                    />
                                                    )}
                                                    {tracklist.length > TRACKLIST_LIMIT && mixes.length !== 1 && (
                                                        <button
                                                            className="tracklist-expand-btn"
                                                            onClick={() => setExpandedTracklistIds(prev =>
                                                                prev.includes(mix.id) ? prev.filter(id => id !== mix.id) : [...prev, mix.id]
                                                            )}
                                                        >
                                                            {expandedTracklistIds.includes(mix.id) ? 'Show less' : `Show all ${tracklist.length}`}
                                                        </button>
                                                    )}
                                                </span>
                                            </h3>
                                        <DJPalScrollableList maxHeight="none">
                                            {(() => {
                                                const trackItems = tracklist.map(e => e.track || e);
                                                const visibleTracklist = expandedTracklistIds.includes(mix.id) || tracklist.length <= TRACKLIST_LIMIT
                                                    ? tracklist
                                                    : tracklist.slice(0, TRACKLIST_LIMIT);
                                                return visibleTracklist.map((entry, idx) => {
                                                const t = entry.track || entry;
                                                const pos = entry.track_position || entry.position || idx + 1;
                                                const trackArt = t.artwork || t.artworkUrl || t.image;
                                                const previewId = `${mix.id}-${idx}`;
                                                return (
                                                    <div
                                                        key={idx}
                                                        className="djpal-track-row clickable"
                                                        onContextMenu={openRowCtx(`tl-${mix.id}-${idx}`)}
                                                        {...delayedClick(() => handleSearchTrack({ stopPropagation: () => {} }, t))}
                                                    >
                                                        <div className="track-meta" style={{ width: 28, textAlign: 'right' }}>{pos}</div>
                                                        <DJPalAudioPreview src={t.previewUrl || t.sampleUrl || t.preview_url || t.preview} youtubeId={t.youtubeId} soundcloudUrl={t.soundcloudUrl} trackId={t.id} durationSec={t.durationSec ?? t.duration} previewId={previewId} trackName={t.title} trackArtist={t.artist} trackCover={trackArt} playContext={{ items: trackItems, index: idx, type: 'tracks' }} playbackBlocked={t.playbackBlocked}>
                                                            <div className="track-artwork">
                                                                {trackArt ? <CoverArt src={trackArt} size={48} /> : <div className="artwork-placeholder">&#9835;</div>}
                                                            </div>
                                                        </DJPalAudioPreview>
                                                        <div className="track-info">
                                                            <span className="track-label" title={t.artist ? `${t.artist} – ${t.title}` : t.title}>{hl(t.title)}</span>
                                                            {(t.artist || (t.artists && t.artists.length > 0)) && (
                                                                <span className="track-sublabel track-artist-line">
                                                                    <MultiArtistText
                                                                        artists={t.artists}
                                                                        fallbackText={t.artist}
                                                                        segmentClassName="clickable-artist"
                                                                        onSelect={(a) => handleArtistClick(a.name, entityId(a), a.artworkUrl)}
                                                                    />
                                                                </span>
                                                            )}
                                                        </div>
                                                        <LikeButton track={t} />
                                                        <TrackContextMenu track={t} onSearch={handleSearchTrack} desktopLike openAtPoint={rowCtxFor(`tl-${mix.id}-${idx}`)} />
                                                    </div>
                                                );
                                            });
                                            })()}
                                        </DJPalScrollableList>
                                        </div>
                                    )}
                                </div>
                            );
                        }

                        const canPlay = canPlayMix(mix);
                        return (
                            <div key={mix.id} className="djpal-mix-card">
                                <div className="djpal-track-row clickable mix-card-header" onContextMenu={openRowCtx(`card-${mix.id}`)} {...cardPointerHandlers(mix)}>
                                    <div
                                        className={`track-artwork${canPlay ? ' clickable' : ''}`}
                                        onClick={canPlay ? (e) => { e.stopPropagation(); handleMixPlay(mix, { items: mixes, index: mixIdx }); } : undefined}
                                    >
                                        {canPlay && (
                                            <div className={`mix-play-overlay${isLoading ? ' loading' : ''}${isPlaying ? ' playing' : ''}`}>
                                                {isLoading ? <span className="mix-play-spinner" /> : <span className={`play-icon${isPlaying ? ' paused' : ''}`}>{isPlaying ? <PauseIcon /> : <PlayIcon />}</span>}
                                            </div>
                                        )}
                                        {art ? <CoverArt src={art} covers={mix.covers} size={48} fallback={<div className="artwork-placeholder">&#9835;</div>} /> : <div className="artwork-placeholder">&#9835;</div>}
                                    </div>
                                    <div className="track-info">
                                        <span className="track-label" title={djLabel ? `${djLabel} - ${mix.title}` : mix.title}>{hl(mix.title || djLabel)}</span>
                                        {(mix.artists?.length > 0 || djLabel || mix.date) && (
                                            <span className="track-sublabel track-artist-line">
                                                <MultiArtistText
                                                    artists={mix.artists}
                                                    fallbackText={djLabel}
                                                    segmentClassName="clickable-artist"
                                                    onSelect={(a) => handleArtistClick(a.name, entityId(a), a.artworkUrl)}
                                                    renderName={hl}
                                                />
                                                {/* v2: where it was (when the title does not already say),
                                                    then the date as the rest of v2 writes it. Two "Coachella
                                                    Festival" rows by the same DJ told apart only by date read
                                                    as duplicates (Erez 11-02). */}
                                                {v2 && mix.location && mix.location !== mix.title && mix.location !== mix.eventName && (
                                                    <>{(mix.artists?.length > 0 || djLabel) ? ' · ' : ''}{mix.location}</>
                                                )}
                                                {(mix.artists?.length > 0 || djLabel || (v2 && mix.location)) && mix.date ? ' · ' : ''}
                                                {v2 ? relativeDate(mix.date) : mix.date}
                                            </span>
                                        )}
                                    </div>
                                    {/* Match the "Mixes by them / featuring them" row pattern: a
                                        hover-visible (desktop) / context-only (mobile) like button
                                        + the ⋯ menu with "Add to Next Up" and "Copy mix name". Two
                                        always-on buttons (copy + queue) made the row's tap target
                                        cramped and ate the first tap on small phones. */}
                                    <MixLikeButton mix={mix} />
                                    <MixContextMenu mix={mix} openAtPoint={rowCtxFor(`card-${mix.id}`)} />
                                </div>
                            </div>
                        );
                    })}
                    {!canAccess && allMixes.length > FREE_RESULTS_LIMIT && (
                        <button className="djpal-tier-lock-msg" onClick={() => dispatch(showUpgradeModal({ feature: 'full-mixpedia', context: { total: allMixes.length } }))}>See all {allMixes.length} results with <GoatBadge /></button>
                    )}
                    {mixFilterContext && mixResultsPagination?.hasNext && !eventOverview && (
                        <div ref={loadMoreSentinelRef} className="djpal-load-more-sentinel">
                            {loadingMore && <span className="mix-play-spinner" />}
                        </div>
                    )}
                </div>
            )}

            {v2 && isEventPage && eventModal === 'djs' && (
                <ArtistsListModal
                    title={mixFilterContext.eventKind === 'series' ? 'Hosts and guests' : 'DJs who played here'}
                    subtitle={mixFilterContext.titleQuery}
                    artists={eventDjs.map(d => ({ name: d.name, artworkUrl: d.art, publicId: d.id, lastPlayedDate: d.rawDate, count: d.count }))}
                    getSubtitle={(a) => `${a.count} ${eventNoun(a.count)}`}
                    sortModes={['recent', 'alphabetical']}
                    onArtistClick={(a) => { setEventModal(null); handleArtistClick(a.name, a.publicId || null, a.artworkUrl || null); }}
                    onClose={() => setEventModal(null)}
                />
            )}
            {v2 && isEventPage && eventModal === 'mixes' && (
                <MixesListModal
                    title="Latest mixes"
                    subtitle={mixFilterContext.titleQuery}
                    section="byArtist"
                    baseFilters={mixFilterContext.sourceId ? { source: mixFilterContext.sourceId } : { title: mixFilterContext.titleQuery }}
                    dateRange={dateRange}
                    initialMixes={allMixes}
                    initialPagination={mixResultsPagination}
                    totalCount={eventTotal || allMixes.length}
                    onMixClick={(mx) => { setEventModal(null); handleOpenMixPage(mx); }}
                    onTrackClick={(t) => handleSearchTrack({ stopPropagation: () => {} }, t)}
                    onArtistClick={(a) => { setEventModal(null); handleArtistClick(a.name, entityId(a), a.artworkUrl); }}
                    onClose={() => setEventModal(null)}
                />
            )}
            {allArtistsModal && (
                <ArtistsListModal
                    title="Artists in this mix"
                    subtitle={[allArtistsModal.djName, allArtistsModal.mixTitle].filter(Boolean).join(' · ') || null}
                    artists={[...allArtistsModal.artists].sort((a, b) => (b.popularityScore || 0) - (a.popularityScore || 0))}
                    sortModes={['popularity', 'alphabetical']}
                    onArtistClick={(a) => { setAllArtistsModal(null); handleArtistClick(a.name, entityId(a)); }}
                    onClose={() => setAllArtistsModal(null)}
                />
            )}

        </div>
    );
};

export default MixSearch;
